import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MessageCircle, Send, Phone, User, Search, CheckCheck, Check,
  Clock, LinkIcon, X, UserPlus, FileText, RefreshCw, XCircle, RotateCcw
} from 'lucide-react';
import toast from 'react-hot-toast';
import { whatsappAPI, beneficiariosAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const WhatsApp = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State
  const [conversas, setConversas] = useState([]);
  const [conversaSelecionada, setConversaSelecionada] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState(null);
  const [buscaTermo, setBuscaTermo] = useState('');
  const [showVincular, setShowVincular] = useState(false);
  const [cpfVincular, setCpfVincular] = useState('');
  const [resultadosBusca, setResultadosBusca] = useState([]);

  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);
  const inputRef = useRef(null);

  // Carregar conversas
  const carregarConversas = useCallback(async () => {
    try {
      const response = await whatsappAPI.listarConversas(filtroStatus);
      setConversas(response.data);
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
    }
    setLoading(false);
  }, [filtroStatus]);

  // Carregar mensagens da conversa selecionada
  const carregarMensagens = useCallback(async () => {
    if (!conversaSelecionada) return;
    try {
      const response = await whatsappAPI.listarMensagens(conversaSelecionada.id);
      setMensagens(response.data);
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    }
  }, [conversaSelecionada]);

  useEffect(() => {
    carregarConversas();
  }, [carregarConversas]);

  // Auto-selecionar conversa via query param ?conversa=ID
  useEffect(() => {
    const conversaId = searchParams.get('conversa');
    if (conversaId && conversas.length > 0 && !conversaSelecionada) {
      const found = conversas.find(c => c.id === parseInt(conversaId));
      if (found) {
        setConversaSelecionada(found);
      }
    }
  }, [searchParams, conversas, conversaSelecionada]);

  useEffect(() => {
    if (conversaSelecionada) {
      carregarMensagens();
    }
  }, [conversaSelecionada, carregarMensagens]);

  // Polling a cada 5s
  useEffect(() => {
    pollingRef.current = setInterval(() => {
      carregarConversas();
      if (conversaSelecionada) {
        carregarMensagens();
      }
    }, 5000);
    return () => clearInterval(pollingRef.current);
  }, [carregarConversas, carregarMensagens, conversaSelecionada]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  // Focus input ao selecionar conversa
  useEffect(() => {
    if (conversaSelecionada) {
      inputRef.current?.focus();
    }
  }, [conversaSelecionada]);

  // Enviar mensagem
  const handleEnviar = async (e) => {
    e.preventDefault();
    if (!novaMensagem.trim() || !conversaSelecionada || enviando) return;

    setEnviando(true);
    try {
      await whatsappAPI.enviarMensagem(conversaSelecionada.id, novaMensagem.trim());
      setNovaMensagem('');
      await carregarMensagens();
      await carregarConversas();
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
      console.error(error);
    }
    setEnviando(false);
  };

  // Vincular beneficiario
  const handleBuscarCpf = async () => {
    if (cpfVincular.length < 3) return;
    try {
      const response = await beneficiariosAPI.search(cpfVincular);
      setResultadosBusca(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleVincular = async (beneficiarioId) => {
    try {
      await whatsappAPI.vincular(conversaSelecionada.id, { beneficiario_id: beneficiarioId });
      toast.success('Beneficiario vinculado!');
      setShowVincular(false);
      setCpfVincular('');
      setResultadosBusca([]);
      await carregarConversas();
      // Atualizar conversa selecionada
      const resp = await whatsappAPI.getConversa(conversaSelecionada.id);
      setConversaSelecionada(resp.data);
    } catch (error) {
      toast.error('Erro ao vincular');
    }
  };

  const handleFinalizar = async () => {
    if (!conversaSelecionada) return;
    try {
      await whatsappAPI.finalizar(conversaSelecionada.id);
      toast.success('Conversa finalizada');
      setConversaSelecionada(null);
      setMensagens([]);
      await carregarConversas();
    } catch (error) {
      toast.error('Erro ao finalizar');
    }
  };

  const handleReabrir = async () => {
    if (!conversaSelecionada) return;
    try {
      await whatsappAPI.reabrir(conversaSelecionada.id);
      toast.success('Conversa reaberta');
      await carregarConversas();
      const resp = await whatsappAPI.getConversa(conversaSelecionada.id);
      setConversaSelecionada(resp.data);
    } catch (error) {
      toast.error('Erro ao reabrir');
    }
  };

  // Filtrar conversas pelo termo de busca
  const conversasFiltradas = conversas.filter(c => {
    if (!buscaTermo) return true;
    const termo = buscaTermo.toLowerCase();
    return (
      (c.nome_contato || '').toLowerCase().includes(termo) ||
      (c.telefone || '').includes(termo) ||
      (c.beneficiario_nome || '').toLowerCase().includes(termo)
    );
  });

  const formatarHora = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const hoje = new Date();
    const isHoje = d.toDateString() === hoje.toDateString();
    if (isHoje) {
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) +
      ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const statusIcon = (status) => {
    switch (status) {
      case 'read': return <CheckCheck size={14} style={{ color: '#3498db' }} />;
      case 'delivered': return <CheckCheck size={14} style={{ color: '#999' }} />;
      case 'sent':
      case 'enviada': return <Check size={14} style={{ color: '#999' }} />;
      default: return <Clock size={14} style={{ color: '#999' }} />;
    }
  };

  if (loading) {
    return (
      <div className="loading" style={{ padding: '2rem' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', background: '#f0f2f5', margin: '-2rem', borderRadius: '8px', overflow: 'hidden' }}>
      {/* Sidebar - Lista de Conversas */}
      <div style={{
        width: '380px',
        minWidth: '380px',
        background: '#fff',
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '12px 16px',
          background: 'var(--brand, #20a490)',
          color: 'var(--brand-ink, #fff)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <MessageCircle size={22} />
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>WhatsApp</h3>
          <button
            onClick={carregarConversas}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}
            title="Atualizar"
          >
            <RefreshCw size={18} />
          </button>
        </div>

        {/* Busca */}
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #e0e0e0' }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            <input
              type="text"
              placeholder="Buscar conversa..."
              value={buscaTermo}
              onChange={(e) => setBuscaTermo(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 8px 8px 32px',
                border: 'none',
                borderRadius: '8px',
                background: '#f0f2f5',
                fontSize: '0.85rem',
                outline: 'none'
              }}
            />
          </div>
        </div>

        {/* Filtros */}
        <div style={{ display: 'flex', gap: '4px', padding: '6px 12px', borderBottom: '1px solid #e0e0e0' }}>
          {[
            { label: 'Todas', value: null },
            { label: 'Abertas', value: 'aberta' },
            { label: 'Finalizadas', value: 'finalizada' },
          ].map(f => (
            <button
              key={f.label}
              onClick={() => setFiltroStatus(f.value)}
              style={{
                padding: '4px 10px',
                borderRadius: '12px',
                border: 'none',
                fontSize: '0.75rem',
                cursor: 'pointer',
                background: filtroStatus === f.value ? 'var(--brand, #20a490)' : '#e8e8e8',
                color: filtroStatus === f.value ? '#fff' : '#555',
                fontWeight: filtroStatus === f.value ? 600 : 400,
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Lista */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {conversasFiltradas.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>
              <MessageCircle size={40} style={{ opacity: 0.3 }} />
              <p style={{ marginTop: '0.5rem' }}>Nenhuma conversa</p>
            </div>
          ) : (
            conversasFiltradas.map(c => (
              <div
                key={c.id}
                onClick={() => setConversaSelecionada(c)}
                style={{
                  display: 'flex',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                  background: conversaSelecionada?.id === c.id ? '#f0f6f5' : '#fff',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { if (conversaSelecionada?.id !== c.id) e.currentTarget.style.background = '#f5f5f5' }}
                onMouseLeave={e => { if (conversaSelecionada?.id !== c.id) e.currentTarget.style.background = '#fff' }}
              >
                {/* Avatar */}
                <div style={{
                  width: '45px',
                  height: '45px',
                  borderRadius: '50%',
                  background: c.beneficiario_id ? 'var(--brand, #20a490)' : '#aaa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  flexShrink: 0
                }}>
                  {(c.nome_contato || c.telefone || '?')[0].toUpperCase()}
                </div>

                {/* Info */}
                <div style={{ marginLeft: '12px', flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#111' }}>
                      {c.beneficiario_nome || c.nome_contato || c.telefone}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: '#999' }}>
                      {formatarHora(c.ultima_mensagem_em)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                    <span style={{
                      fontSize: '0.8rem',
                      color: '#666',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '200px'
                    }}>
                      {c.ultima_mensagem || 'Sem mensagens'}
                    </span>
                    {c.status === 'finalizada' && (
                      <span style={{
                        fontSize: '0.65rem',
                        padding: '1px 6px',
                        borderRadius: '8px',
                        background: '#e0e0e0',
                        color: '#666'
                      }}>
                        Finalizada
                      </span>
                    )}
                  </div>
                  {c.beneficiario_nome && c.nome_contato && c.beneficiario_nome !== c.nome_contato && (
                    <span style={{ fontSize: '0.7rem', color: 'var(--brand, #20a490)' }}>
                      {c.telefone}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#e5ddd5' }}>
        {!conversaSelecionada ? (
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            background: '#f0f2f5'
          }}>
            <MessageCircle size={64} style={{ opacity: 0.2, marginBottom: '1rem' }} />
            <h3 style={{ fontWeight: 400, color: '#666' }}>Selecione uma conversa</h3>
            <p style={{ fontSize: '0.85rem' }}>Escolha uma conversa na lista para comecar</p>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div style={{
              padding: '10px 16px',
              background: '#fff',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: conversaSelecionada.beneficiario_id ? 'var(--brand, #20a490)' : '#aaa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 600
              }}>
                {(conversaSelecionada.nome_contato || '?')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {conversaSelecionada.beneficiario_nome || conversaSelecionada.nome_contato || conversaSelecionada.telefone}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#666', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Phone size={12} />
                  {conversaSelecionada.telefone}
                  {conversaSelecionada.protocolo && (
                    <span style={{ color: 'var(--brand, #20a490)' }}>
                      <FileText size={12} style={{ marginRight: '2px' }} />
                      {conversaSelecionada.protocolo}
                    </span>
                  )}
                </div>
              </div>

              {/* Acoes */}
              <div style={{ display: 'flex', gap: '4px' }}>
                {!conversaSelecionada.beneficiario_id && (
                  <button
                    onClick={() => setShowVincular(true)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid var(--brand, #20a490)',
                      background: '#fff',
                      color: 'var(--brand, #20a490)',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <LinkIcon size={14} />
                    Vincular
                  </button>
                )}
                {conversaSelecionada.beneficiario_id && (
                  <button
                    onClick={() => {
                      const cpf = conversaSelecionada.beneficiario_nome; // Navigate uses beneficiario_id
                      navigate(`/beneficiario/${conversaSelecionada.beneficiario_id}`);
                    }}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #3498db',
                      background: '#fff',
                      color: '#3498db',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <User size={14} />
                    Perfil
                  </button>
                )}
                {conversaSelecionada.status !== 'finalizada' ? (
                  <button
                    onClick={handleFinalizar}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #e74c3c',
                      background: '#fff',
                      color: '#e74c3c',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <XCircle size={14} />
                    Finalizar
                  </button>
                ) : (
                  <button
                    onClick={handleReabrir}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #27ae60',
                      background: '#fff',
                      color: '#27ae60',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <RotateCcw size={14} />
                    Reabrir
                  </button>
                )}
              </div>
            </div>

            {/* Mensagens */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'200\' height=\'200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'a\' patternUnits=\'userSpaceOnUse\' width=\'40\' height=\'40\'%3E%3Cpath d=\'M0 20h40M20 0v40\' fill=\'none\' stroke=\'%23dcd6cc\' stroke-width=\'.3\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'100%25\' height=\'100%25\' fill=\'%23e5ddd5\'/%3E%3Crect width=\'100%25\' height=\'100%25\' fill=\'url(%23a)\'/%3E%3C/svg%3E")',
            }}>
              {mensagens.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', marginTop: '2rem' }}>
                  <p>Nenhuma mensagem ainda</p>
                </div>
              ) : (
                mensagens.map((msg) => (
                  <div
                    key={msg.id}
                    style={{
                      display: 'flex',
                      justifyContent: msg.direcao === 'saida' ? 'flex-end' : 'flex-start',
                      marginBottom: '4px',
                    }}
                  >
                    <div style={{
                      maxWidth: '65%',
                      padding: '6px 10px 4px',
                      borderRadius: msg.direcao === 'saida' ? '8px 0 8px 8px' : '0 8px 8px 8px',
                      background: msg.direcao === 'saida' ? '#dcf8c6' : '#fff',
                      boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
                      position: 'relative',
                    }}>
                      {msg.direcao === 'saida' && msg.enviado_por_nome && (
                        <div style={{ fontSize: '0.7rem', color: 'var(--brand, #20a490)', fontWeight: 600, marginBottom: '2px' }}>
                          {msg.enviado_por_nome}
                        </div>
                      )}
                      <div style={{ fontSize: '0.88rem', color: '#111', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {msg.conteudo}
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: '4px',
                        marginTop: '2px'
                      }}>
                        <span style={{ fontSize: '0.65rem', color: '#999' }}>
                          {formatarHora(msg.criado_em)}
                        </span>
                        {msg.direcao === 'saida' && statusIcon(msg.status_envio)}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            {conversaSelecionada.status !== 'finalizada' ? (
              <form onSubmit={handleEnviar} style={{
                display: 'flex',
                padding: '8px 16px',
                background: '#f0f0f0',
                gap: '8px',
                alignItems: 'center'
              }}>
                <input
                  ref={inputRef}
                  type="text"
                  value={novaMensagem}
                  onChange={(e) => setNovaMensagem(e.target.value)}
                  placeholder="Digite uma mensagem..."
                  disabled={enviando}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    borderRadius: '20px',
                    border: 'none',
                    fontSize: '0.9rem',
                    outline: 'none',
                    background: '#fff',
                  }}
                />
                <button
                  type="submit"
                  disabled={!novaMensagem.trim() || enviando}
                  style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    border: 'none',
                    background: 'var(--brand, #20a490)',
                    color: 'var(--brand-ink, #fff)',
                    cursor: novaMensagem.trim() && !enviando ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: novaMensagem.trim() && !enviando ? 1 : 0.5,
                  }}
                >
                  <Send size={18} />
                </button>
              </form>
            ) : (
              <div style={{
                padding: '12px',
                background: '#f0f0f0',
                textAlign: 'center',
                color: '#999',
                fontSize: '0.85rem'
              }}>
                Conversa finalizada. Clique em "Reabrir" para continuar.
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Vincular */}
      {showVincular && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            width: '450px',
            maxHeight: '500px',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Vincular Beneficiario</h3>
              <button onClick={() => { setShowVincular(false); setCpfVincular(''); setResultadosBusca([]); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <input
                type="text"
                placeholder="Buscar por CPF ou nome..."
                value={cpfVincular}
                onChange={(e) => setCpfVincular(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleBuscarCpf()}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
              />
              <button
                onClick={handleBuscarCpf}
                style={{
                  padding: '8px 16px',
                  background: 'var(--brand, #20a490)',
                  color: 'var(--brand-ink, #fff)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                <Search size={16} />
              </button>
            </div>

            {resultadosBusca.length > 0 && (
              <div>
                {resultadosBusca
                  .filter(b => b.id)
                  .map((b, index) => (
                  <div
                    key={`ben-${b.id}`}
                    onClick={() => handleVincular(b.id)}
                    style={{
                      padding: '10px',
                      borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      borderRadius: '6px',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{b.nome_completo}</div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                      CPF: {b.cpf_mascarado} {b.telefone_principal && `| Tel: ${b.telefone_principal}`}
                    </div>
                  </div>
                ))}
                {resultadosBusca.filter(b => b.id).length === 0 && (
                  <div style={{ textAlign: 'center', color: '#999', padding: '1rem', fontSize: '0.85rem' }}>
                    Beneficiarios encontrados na operadora, mas nao cadastrados no CRM.
                    Crie um atendimento para este cliente primeiro.
                  </div>
                )}
              </div>
            )}

            {cpfVincular && resultadosBusca.length === 0 && (
              <div style={{ textAlign: 'center', color: '#999', padding: '1rem' }}>
                Nenhum beneficiario encontrado
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsApp;
